import { useEffect, useState } from "react";
import { useLocation } from "react-router";

import { Box } from "ui/box";
import { Stack } from "ui/stack";
import { DesignPreview } from "containers/admin/clients/touchpoint/components/design-preview/design-preview";
import EmptyDesignPreview from "containers/admin/clients/touchpoint/components/empty-design-preview/empty-design-preview";
import {
  builderPreviewMenuOptions,
  BuilderPreviewPaneHeader,
  BuilderPreviewViews,
} from "containers/admin/clients/touchpoint/components/builder/builder-preview-pane-header/builder-preview-pane-header";
import { BuilderHtmlPreview } from "containers/admin/clients/touchpoint/components/builder/builder-html-preview/builder-html-preview";
import {
  EmailHeaders,
  initialEmailHeaders,
} from "containers/admin/clients/touchpoint/components/email-builder/email-header-preview/email-header-preview";

import { ISenderDetails } from "containers/admin/clients/client/components/email-types/components/email-types-senders-form/email-types-senders-form.types";

import { getTouchpointVersion } from "generated/api/touchpoint-version";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import {
  useBuilderContent,
  useSetTouchpointVersionCache,
  type EmailAttributes,
} from "hooks/api/use-touchpoint-versions";

import { mapTouchpointVersionDTO } from "utilities/api-mappers";
import { removeUserIdFromString } from "utilities/touchpoint";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";

const touchpointVersionApi = getTouchpointVersion();

const EmailBuilderPreviewTab = ({
  previewMode: isPreviewMode,
  currentSender,
}: {
  previewMode: boolean;
  currentSender: ISenderDetails;
}) => {
  const currentTouchpoint = useCurrentTouchpoint();
  const currentCampaign = useCurrentCampaign();
  const isTemplate = currentCampaign.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME;
  const currentVersion = useBuilderContent() as EmailAttributes;
  const location = useLocation();
  const setTouchpointVersionCache = useSetTouchpointVersionCache({
    clientId: currentTouchpoint.clientId,
    campaignId: currentTouchpoint.campaignId,
    touchpointId: currentTouchpoint.id,
  });
  const [isCommentMode, setIsCommentMode] = useState(false);
  const [isFullContentMode, setIsFullContentMode] = useState(false);

  const { desktopView, mobileView } = builderPreviewMenuOptions;
  const [currentView, setCurrentView] = useState<BuilderPreviewViews>(mobileView);
  const isDesktopPreview = currentView === desktopView;
  const isMobilePreview = currentView === mobileView;
  const isDesignPreview = isDesktopPreview || isMobilePreview;

  useEffect(() => {
    if (isPreviewMode) {
      touchpointVersionApi
        .getOneVersion({
          clientId: currentTouchpoint.clientId,
          campaignId: currentTouchpoint.campaignId,
          touchpointId: currentTouchpoint.id,
          id: currentVersion.id,
        })
        .then(mapTouchpointVersionDTO)
        .then(setTouchpointVersionCache);
    }
    // eslint-disable-next-line
  }, []);

  const emailHeaders: EmailHeaders = {
    senderName: currentSender?.senderName || initialEmailHeaders.senderName,
    replyToEmail: currentSender?.replyToEmail || initialEmailHeaders.replyToEmail,
    subjectLine: currentVersion.subjectLine || initialEmailHeaders.subjectLine,
    preheader: currentVersion.preheader || initialEmailHeaders.preheader,
    senderEmail: currentSender?.senderEmail || initialEmailHeaders.senderEmail,
  };

  const builderTestId = isDesktopPreview
    ? "email-builder-desktop-previewType"
    : isMobilePreview
      ? "email-builder-mobile-previewType"
      : // isHtmlView
        "email-builder-html";

  const maxWidthClass = isMobilePreview ? "max-w-[25rem]" : `max-w-calc(100% - 0.5rem)`;

  const designTabRedirect = location.pathname.replace("preview", "design");
  return (
    <Stack
      data-testid={builderTestId}
      spacing={0}
      sx={{ alignItems: "center", flex: "1", mx: "auto", maxWidth: "1024px", width: "100%" }}
    >
      <BuilderPreviewPaneHeader
        currentView={currentView}
        setCurrentView={setCurrentView}
        isCommentMode={isCommentMode}
        setIsCommentMode={setIsCommentMode}
        isTouchpointPreview={false}
        isFullContentMode={isFullContentMode}
        setIsFullContentMode={setIsFullContentMode}
      />
      <Box className={`w-full ${maxWidthClass}`}>
        {isDesignPreview ? (
          currentVersion.finalHtml && currentVersion.data?.jsonOutput ? (
            <DesignPreview
              htmlContent={removeUserIdFromString(currentVersion.finalHtml)}
              htmlJSON={currentVersion.data?.jsonOutput}
              isCommentMode={isCommentMode}
              isFullContentMode={isFullContentMode}
              mobileView={isMobilePreview}
              emailHeaders={emailHeaders}
              hideSubjectAndPreheader={isTemplate}
            />
          ) : (
            <EmptyDesignPreview redirectTo={designTabRedirect} currentVersion={currentVersion} />
          )
        ) : (
          <BuilderHtmlPreview
            data-testid="email-builder-html"
            textareaTestId="email-builder-advanced-textarea"
            value={currentVersion.finalHtml}
          />
        )}
      </Box>
    </Stack>
  );
};

export default EmailBuilderPreviewTab;
